import { Button } from "@/components/ui/button";
import { ArrowRight, Users, TrendingUp, HandshakeIcon, Coins, Globe2, Sparkles } from "lucide-react";

interface PartnerSectionProps {
  onPartnerCTA: () => void;
}

const benefits = [
  {
    icon: Coins,
    title: "Earn Recurring Revenue",
    description: "Get rewarded every time a trader from your network grows with JouRex.",
  },
  {
    icon: Globe2,
    title: "Share Your Network",
    description: "Bring traders, communities, or signal groups into a smarter ecosystem.",
  },
  {
    icon: Sparkles,
    title: "Win-Win-Win",
    description: "Your traders gain better tools, you earn, and we grow together.",
  },
];

export const PartnerSection = ({ onPartnerCTA }: PartnerSectionProps) => {
  return (
    <section id="partners" className="container mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-50 via-card to-emerald-50/50 ring-1 ring-violet-200/60 shadow-card">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-violet-300/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-300/20 blur-3xl" />

        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 p-8 lg:p-14">
          {/* Left: Visual */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square">
              {/* Center node */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-violet-500/30 blur-2xl rounded-full" />
                  <div className="relative h-24 w-24 rounded-2xl bg-gradient-to-br from-violet-600 to-violet-800 flex items-center justify-center shadow-elevated">
                    <HandshakeIcon className="h-10 w-10 text-white" />
                  </div>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-widest uppercase text-violet-700 bg-card px-2 py-0.5 rounded-full ring-1 ring-violet-200">
                    You
                  </div>
                </div>
              </div>

              {/* Orbit nodes */}
              {[
                { angle: 0, icon: Users, label: "Traders", color: "emerald" },
                { angle: 72, icon: TrendingUp, label: "Growth", color: "primary" },
                { angle: 144, icon: Globe2, label: "Network", color: "violet" },
                { angle: 216, icon: Coins, label: "Revenue", color: "amber" },
                { angle: 288, icon: Sparkles, label: "Tools", color: "rose" },
              ].map((node, i) => {
                const rad = (node.angle * Math.PI) / 180;
                const x = 50 + 38 * Math.cos(rad);
                const y = 50 + 38 * Math.sin(rad);
                return (
                  <div
                    key={i}
                    className="absolute -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${x}%`, top: `${y}%` }}
                  >
                    <div className="h-14 w-14 rounded-xl bg-card shadow-card ring-1 ring-border/60 flex items-center justify-center">
                      <node.icon className="h-6 w-6 text-violet-700" />
                    </div>
                    <div className="mt-1 text-center text-[10px] font-semibold text-foreground/70">
                      {node.label}
                    </div>
                  </div>
                );
              })}

              {/* Connecting lines */}
              <svg className="absolute inset-0 w-full h-full -z-10" viewBox="0 0 100 100">
                {[0, 72, 144, 216, 288].map((angle, i) => {
                  const rad = (angle * Math.PI) / 180;
                  const x = 50 + 38 * Math.cos(rad);
                  const y = 50 + 38 * Math.sin(rad);
                  return (
                    <line
                      key={i}
                      x1="50"
                      y1="50"
                      x2={x}
                      y2={y}
                      stroke="hsl(262 83% 58% / 0.25)"
                      strokeWidth="0.4"
                      strokeDasharray="1 1"
                    />
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Right: Content */}
          <div className="flex flex-col justify-center">
            <span className="inline-flex items-center gap-1.5 self-start text-[11px] font-bold tracking-widest text-violet-700 uppercase mb-3 bg-violet-500/10 px-3 py-1 rounded-full">
              <HandshakeIcon className="h-3 w-3" />
              Affiliate Program
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
              Turn your trader network into a{" "}
              <span className="bg-gradient-to-r from-violet-600 to-violet-800 bg-clip-text text-transparent">
                shared advantage.
              </span>
            </h2>
            <p className="mt-4 text-base text-foreground/70 leading-relaxed">
              Anyone with an active trading community — coaches, signal providers,
              educators or influencers — can become a JouRex partner. Share your network,
              and unlock a true win-win-win: your traders get a smarter platform, you earn
              recurring rewards, and we grow together.
            </p>

            {/* Benefits */}
            <div className="mt-6 space-y-3">
              {benefits.map((b) => (
                <div key={b.title} className="flex items-start gap-3">
                  <div className="shrink-0 h-9 w-9 rounded-lg bg-violet-500/10 ring-1 ring-violet-500/20 flex items-center justify-center">
                    <b.icon className="h-4 w-4 text-violet-700" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">{b.title}</div>
                    <div className="text-sm text-foreground/60">{b.description}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-4">
              <Button
                onClick={onPartnerCTA}
                className="h-12 px-6 rounded-xl bg-gradient-to-r from-violet-700 to-violet-900 hover:from-violet-800 hover:to-violet-950 text-white font-semibold shadow-elevated"
              >
                Become a Partner
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <span className="text-xs text-foreground/50">
                No setup fees · Transparent commissions
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
